import { useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  Camera,
  Barcode,
  Signature,
  Square,
  Check,
  AlertCircle,
  Loader2,
  X,
} from 'lucide-react';
import { Images, Play, Pause, StickyNote } from 'lucide-react';
import { api } from '../../lib/api.js';
import { NotesModal } from './notes-modal.js';
import { completedUdfPatch, FOP_UDF, todayLocalIso } from '../../lib/dispatch-board.js';
import { useOrderVisits } from '../../lib/visits.js';
import { useFieldJob } from '../../lib/field-job.js';
import { enqueueFieldEvent, shouldQueueFieldEvent } from '../../lib/outbox.js';
import { blobToBase64, downscaleImage } from '../../lib/image-upload.js';

/** Mirrors `AddPhotoInputDto.base64` MaxLength on the API (~5.6 MB raw). */
const MAX_PHOTO_BASE64_CHARS = 7_500_000;
import { drainOutbox } from '../../lib/outbox-processor.js';
import {
  ACTIVE_JOB_QUERY_KEY,
  formatTimer,
  nextLinearPhase,
  phaseLabel,
  useActiveJob,
  useOpenEntrySeconds,
} from '../../lib/field-time.js';
import { PartsPicker } from './parts-picker.js';
import { TimeBreakdownPanel } from './time-breakdown.js';
import { JobFiles, jobFilesQueryKey } from '../job-files.js';
import { JobNotes, jobNotesQueryKey } from '../job-notes.js';
import { jobSite } from '../../lib/job-site.js';

/**
 * Live on-site screen — running timer, quick actions, equipment summary.
 *
 * The four action buttons (Photo / Voice / Parts / Sign) mirror the
 * mockup's `.actions` grid. For this slice:
 *  - Photo increments a local counter only (S3 + CRM-note write is queued
 *    for a follow-up); the rep's photo workflow on the SO detail page
 *    already uses the real endpoint.
 *  - Voice is a placeholder.
 *  - Parts opens an inline scanner that adds to a local list (Spire PUT
 *    of new lines requires careful idempotency design — Phase 5 follow-up).
 *  - Sign routes to the signature screen which uploads to Spire as a CRM
 *    note attachment using the existing `attachSignature` endpoint.
 */
export function OnsiteScreen() {
  const orderId = useFieldJob((s) => s.currentOrderId);
  const parts = useFieldJob((s) => s.parts);
  const photos = useFieldJob((s) => s.photos);
  const signed = useFieldJob((s) => s.signed);
  const goSign = useFieldJob((s) => s.goSign);
  const park = useFieldJob((s) => s.parkJob);
  const complete = useFieldJob((s) => s.complete);
  const cancelJob = useFieldJob((s) => s.cancelJob);
  const updatePart = useFieldJob((s) => s.updatePart);
  const removePart = useFieldJob((s) => s.removePart);
  const addPhoto = useFieldJob((s) => s.addPhoto);
  const updatePhoto = useFieldJob((s) => s.updatePhoto);
  const removePhoto = useFieldJob((s) => s.removePhoto);

  const [partsOpen, setPartsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesCount, setNotesCount] = useState(0);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const libraryInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();

  const addNoteMutation = useMutation({
    mutationFn: async (input: { orderId: number; body: string }) => {
      return api.salesOrders.addNote(input.orderId, { body: input.body });
    },
    onSuccess: () => {
      setNotesCount((n) => n + 1);
      setNotesOpen(false);
      void queryClient.invalidateQueries({
        queryKey: ['sales-orders', orderId, 'time-entries'],
      });
      if (orderId !== null) {
        void queryClient.invalidateQueries({ queryKey: jobNotesQueryKey(orderId) });
      }
    },
    onError: async (err, vars) => {
      // Network / 5xx failures get queued for retry — the typed note is
      // not lost on a refresh. The notes modal stays open so the tech
      // sees the inline error, but the outbox will drain it whenever
      // the device gets back online.
      if (shouldQueueFieldEvent(err)) {
        await enqueueFieldEvent({
          spireSalesOrderId: vars.orderId,
          spireOrderNo: orderQuery.data?.orderNo ?? String(vars.orderId),
          payload: { kind: 'addNote', data: { body: vars.body } },
        });
        setNotesCount((n) => n + 1);
        setNotesOpen(false);
        void drainOutbox();
      }
    },
  });

  // Clock-out write: stamps FOP_COMPLETED_AT on the order via the same PUT
  // path the dispatcher uses. Fire-and-forget — we flip to the Complete
  // screen immediately so the tech isn't gated on Spire latency. On
  // success we invalidate the dispatch board's open-orders query so the
  // dispatcher sees the "Done" treatment within the 30s staleTime; on
  // failure we surface a banner but stay on the Complete screen since
  // the visit's parts + signature already wrote.
  const markComplete = useMutation({
    mutationFn: (input: { orderId: number; at: Date }) =>
      api.salesOrders.update(input.orderId, { udf: completedUdfPatch(input.at) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['operations', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['dispatch', 'open-orders'] });
    },
  });

  // One Spire upload per captured photo. The file input triggers
  // addPhoto locally with the encoded base64, then this mutation pushes
  // the bytes to the salesOrders.addPhoto endpoint (which creates a CRM
  // note attachment on the order).
  const uploadPhoto = useMutation({
    mutationFn: async (input: {
      clientId: string;
      orderId: number;
      contentType: 'image/png' | 'image/jpeg';
      base64: string;
      filename: string;
      caption?: string;
    }) => {
      updatePhoto(input.clientId, { syncStatus: 'syncing', lastError: null });
      const res = await api.salesOrders.addPhoto(input.orderId, {
        contentType: input.contentType,
        base64: input.base64,
        filename: input.filename,
        ...(input.caption ? { caption: input.caption } : {}),
      });
      return { clientId: input.clientId, ...res };
    },
    onSuccess: (res, vars) => {
      updatePhoto(res.clientId, {
        syncStatus: 'synced',
        spireNoteId: res.noteId,
      });
      void queryClient.invalidateQueries({ queryKey: jobFilesQueryKey(vars.orderId) });
    },
    onError: async (err: unknown, vars) => {
      updatePhoto(vars.clientId, {
        syncStatus: 'failed',
        lastError: err instanceof Error ? err.message : String(err),
      });
      // Network / 5xx — queue the photo bytes to IndexedDB so a refresh
      // doesn't kill the retry path. The zustand entry's base64 gets
      // stripped on persist; the outbox row keeps the full payload.
      if (shouldQueueFieldEvent(err)) {
        await enqueueFieldEvent({
          spireSalesOrderId: vars.orderId,
          spireOrderNo: orderQuery.data?.orderNo ?? String(vars.orderId),
          payload: {
            kind: 'addPhoto',
            data: {
              contentType: vars.contentType,
              base64: vars.base64,
              filename: vars.filename,
              ...(vars.caption ? { caption: vars.caption } : {}),
            },
          },
          localClientId: vars.clientId,
        });
        void drainOutbox();
      }
    },
  });

  // One Spire append per captured part. The picker calls onPart after
  // dispatching addPart locally; we use the returned clientId to track
  // sync state on the same record.
  const appendPart = useMutation({
    mutationFn: async (input: {
      clientId: string;
      orderId: number;
      partNo: string;
      whse: string;
      qty: string;
      unitPrice: string;
      description: string;
    }) => {
      updatePart(input.clientId, { syncStatus: 'syncing', lastError: null });
      const res = await api.salesOrders.addItem(input.orderId, {
        whse: input.whse,
        partNo: input.partNo,
        orderQty: input.qty,
        unitPrice: input.unitPrice !== '0' ? input.unitPrice : undefined,
        description: input.description || undefined,
        source: 'field',
      });
      return { clientId: input.clientId, ...res };
    },
    onSuccess: (res) => {
      updatePart(res.clientId, {
        syncStatus: 'synced',
        spireLineId: res.lineId,
        spireSequence: res.sequence,
      });
    },
    onError: async (err: unknown, vars) => {
      updatePart(vars.clientId, {
        syncStatus: 'failed',
        lastError: err instanceof Error ? err.message : String(err),
      });
      if (shouldQueueFieldEvent(err)) {
        await enqueueFieldEvent({
          spireSalesOrderId: vars.orderId,
          spireOrderNo: orderQuery.data?.orderNo ?? String(vars.orderId),
          payload: {
            kind: 'addItem',
            data: {
              whse: vars.whse,
              partNo: vars.partNo,
              orderQty: vars.qty,
              ...(vars.unitPrice !== '0' ? { unitPrice: vars.unitPrice } : {}),
              ...(vars.description ? { description: vars.description } : {}),
            },
          },
          localClientId: vars.clientId,
        });
        void drainOutbox();
      }
    },
  });

  const orderQuery = useQuery({
    queryKey: ['sales-orders', 'detail', orderId],
    queryFn: ({ signal }) => api.salesOrders.getById(orderId!, signal),
    enabled: orderId !== null,
    staleTime: 30_000,
  });

  // Server-side phase + per-phase timer. The phase strip displays the
  // current open entry's phase + an Open-entry timer driven by the local
  // 1Hz tick (`openSec`). The total-on-job KPI is computed by the API and
  // recomputed here whenever the active entry changes.
  const activeQuery = useActiveJob();
  const phase = activeQuery.data?.currentPhase ?? null;
  const openSec = useOpenEntrySeconds(activeQuery.data ?? null);
  const nextPhase = phase ? nextLinearPhase(phase) : null;
  const phaseMutation = useMutation({
    mutationFn: (input: { phase: 'on_site' | 'work_in_progress' | 'paused' | 'work_completed' }) =>
      api.field.phaseChange({ phase: input.phase }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACTIVE_JOB_QUERY_KEY });
    },
  });

  // Clock-out runs two writes in series: (a) close the open TimeEntry +
  // clear the lock + mirror FOP_PHASE=signed_off, then (b) mirror
  // FOP_COMPLETED_AT on the order header so the dispatcher's "Done"
  // treatment lights up. We AWAIT both via mutateAsync so the Complete
  // screen only flips on full success — a fire-and-forget here would
  // show "Signature and N parts written to Spire" even when the writes
  // 5xx'd, leaving the dispatcher's board with the wrong story.
  const stopMutation = useMutation({
    mutationFn: () => api.field.stop({ finalPhase: 'signed_off' }),
  });

  // Multi-tech: only the LEAD's clock-out writes FOP_COMPLETED_AT. Helpers
  // close their own TimeEntry without flipping the order's Done flag —
  // the visit remains in-progress until the lead clocks out. me.techId
  // tells us who we are; FOP_TECH on the order tells us who's lead.
  //
  // NOTE: every hook must stay ABOVE the `orderId === null` early return —
  // hooks after a conditional return violate the rules of hooks and crash
  // the component when orderId flips between null/non-null while mounted.
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me.get(signal),
    staleTime: 5 * 60_000,
  });
  // Multi-visit jobs: when another visit is booked after today, the lead can
  // end the day WITHOUT completing — the clock stops and the lock clears,
  // but no signature is needed and fop_completed_at stays empty. `paused`
  // (not `scheduled`) so the board shows work has started.
  const orderVisitsQuery = useOrderVisits(orderId);
  const nextVisit = (orderVisitsQuery.data ?? []).find((v) => v.date > todayLocalIso()) ?? null;
  const endDayMutation = useMutation({
    mutationFn: () => api.field.stop({ finalPhase: 'paused' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACTIVE_JOB_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['field', 'day-list'] });
      cancelJob();
    },
  });
  const [clockOutError, setClockOutError] = useState<string | null>(null);
  // Clock-out is two server writes (stop, then markComplete for leads).
  // Track stop's success so Retry after a markComplete failure does NOT
  // re-run stop — the server has no open entry at that point and would
  // 400 forever (audit P0-7).
  const stopDoneRef = useRef<{ totalSec: number } | null>(null);

  if (orderId === null) {
    return <div className="text-sm text-ink-muted">No active job.</div>;
  }

  const order = orderQuery.data;
  const customerName = order?.customer.name ?? 'Loading…';
  const site = order ? jobSite(order) : null;
  const address = site?.addressLine ?? '';

  const partsTotal = parts.reduce((sum, p) => sum + Number(p.qty) * Number(p.unitPrice), 0);

  const viewerTechId = meQuery.data?.techId ?? null;
  const leadTechId = order?.udf?.[FOP_UDF.tech]?.trim() ?? null;
  const isLead = leadTechId !== null && viewerTechId !== null && leadTechId === viewerTechId;
  const onComplete = async () => {
    // Lead must have a signature before clocking out (closes the visit
    // on the SO). Helpers can stop their own time at any point — no
    // signature gating, no FOP_COMPLETED_AT write.
    if (!order || orderId === null) return;
    if (isLead && !signed) return;
    setClockOutError(null);
    try {
      // Stop FIRST so the server-side TimeEntry closes + the lock clears
      // even if the FOP_COMPLETED_AT mirror later fails. Order of
      // operations matters: we'd rather have an order with an open
      // TimeEntry + no Done-flag than a Done-flag with an open entry.
      // Server returns `{ entries, totalSec }` — totalSec is the
      // authoritative total across all this visit's phases, which is
      // what the Complete summary should show (matches what the office
      // will see in the Time-on-job panel).
      //
      // Skip stop on retry when it already succeeded — re-running it
      // would 400 (no open entry) and dead-end the clock-out forever.
      if (stopDoneRef.current === null) {
        const stopResult = await stopMutation.mutateAsync();
        stopDoneRef.current = { totalSec: stopResult.totalSec };
      }
      // Only the LEAD's clock-out marks the visit complete on the order.
      // Helpers close their TimeEntry but the order stays in-progress
      // until the lead finishes — the office sees one Done flag, not N.
      if (isLead) {
        await markComplete.mutateAsync({ orderId, at: new Date() });
      }
      void queryClient.invalidateQueries({ queryKey: ACTIVE_JOB_QUERY_KEY });
      complete({
        orderNo: order.orderNo,
        customerName: order.customer.name,
        elapsedSec: stopDoneRef.current.totalSec,
        partsCount: parts.length,
        photoCount: photos.length,
        total: order.total,
        completedAt: Date.now(),
      });
    } catch (err) {
      setClockOutError(err instanceof Error ? err.message : 'Failed to close out — try again.');
    }
  };
  const clockOutPending = stopMutation.isPending || markComplete.isPending;

  const onPhotoSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    // Copy before the reset below — clearing `value` empties the live FileList.
    const files = Array.from(e.target.files ?? []);
    // Always reset the input so picking the same file twice re-fires.
    e.target.value = '';
    if (orderId === null) return;
    // The camera input yields one file; the library picker can yield several.
    for (const file of files) await addPhotoFile(file, orderId);
  };

  const addPhotoFile = async (file: File, orderId: number) => {
    // Re-encode to <=2560px JPEG like the quote builder does. A raw camera
    // photo (5-8 MB on a current phone) base64-encodes past the API's
    // 7.5M-char cap and came back as a bare "HTTP 400" — techs reported
    // "can't add pictures" on 2026-09-25 with nothing in the server logs.
    const shrunk = await downscaleImage(file);
    const source: Blob = shrunk ?? file;
    const contentType: 'image/png' | 'image/jpeg' =
      !shrunk && (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png'))
        ? 'image/png'
        : 'image/jpeg';
    let base64: string;
    try {
      base64 = await blobToBase64(source);
    } catch {
      return;
    }
    const baseName = (file.name || `field-photo-${new Date().toISOString().replace(/[:.]/g, '-')}`)
      .replace(/\.[^.]+$/, '');
    const filename = `${baseName}.${contentType === 'image/png' ? 'png' : 'jpg'}`;
    const sizeKb = Math.round((base64.length * 3) / 4 / 1024);
    const clientId = addPhoto({
      filename,
      contentType,
      base64,
      caption: '',
      sizeKb,
    });
    if (base64.length > MAX_PHOTO_BASE64_CHARS) {
      // Still too big after downscaling (a huge PNG, or a browser that
      // couldn't decode the file). Fail locally with a real reason instead
      // of a 400 from the API.
      updatePhoto(clientId, {
        syncStatus: 'failed',
        lastError: `Photo is too large (${Math.round(sizeKb / 1024)} MB) — the limit is about 5 MB. Try a screenshot of it, or a lower camera resolution.`,
      });
      return;
    }
    uploadPhoto.mutate({
      clientId,
      orderId,
      contentType,
      base64,
      filename,
    });
  };

  return (
    <div className="space-y-3">
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={park}
          aria-label="Back to my day (keep job open)"
          title="Back to my day — keeps the timer running"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="text-sm font-medium text-ink truncate max-w-[60%]">{customerName}</div>
        {/* Reserve the right slot for symmetric header layout — wired
           actions can land here when we have them (per-job menu, etc). */}
        <div className="h-9 w-9" aria-hidden />
      </header>

      <div
        className="rounded-xl p-4 text-white space-y-3"
        style={{ backgroundColor: 'var(--color-live-card-bg)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--trade-pm)' }}>
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-white" aria-hidden />
            {phase ? phaseLabel(phase) : 'On site'}
          </span>
          <span className="text-xs opacity-85 font-mono">{order?.orderNo ?? ''}</span>
        </div>
        <div className="text-3xl font-semibold tabular-nums tracking-tight">
          {formatTimer(openSec)}
        </div>
        {address && <div className="text-xs opacity-85">on clock at {address}</div>}

        {phase && phase !== 'work_completed' && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <PhaseButton
              label={phase === 'paused' ? 'Resume work' : 'Pause'}
              Icon={phase === 'paused' ? Play : Pause}
              disabled={phaseMutation.isPending}
              onClick={() =>
                phaseMutation.mutate({
                  phase: phase === 'paused' ? 'work_in_progress' : 'paused',
                })
              }
            />
            {/* "Next" advances the linear workflow when one's defined.
               Suppressed from `paused` (you Resume, not advance) and
               from anywhere nextPhase would be `signed_off` (Clock-out
               below owns that transition, with proper stop semantics
               instead of an extra phase-change). */}
            {nextPhase && nextPhase !== 'signed_off' && (
              <PhaseButton
                label={`Next: ${phaseLabel(nextPhase)}`}
                Icon={Check}
                disabled={phaseMutation.isPending}
                onClick={() => {
                  phaseMutation.mutate({
                    phase: nextPhase as
                      | 'on_site'
                      | 'work_in_progress'
                      | 'work_completed',
                  });
                }}
              />
            )}
          </div>
        )}

        {/* Recovery path: tech marked work complete but realized they
           forgot a part / step. Drops them back into work_in_progress
           so the phase strip + Pause/Next return, instead of dead-
           ending into the gated Clock-out CTA. */}
        {phase === 'work_completed' && (
          <button
            type="button"
            onClick={() => phaseMutation.mutate({ phase: 'work_in_progress' })}
            disabled={phaseMutation.isPending}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold border border-white/30 bg-white/10 hover:bg-white/20 disabled:opacity-50"
          >
            <Play className="h-4 w-4" aria-hidden />
            Back to work
          </button>
        )}

        {/* A failed phase change must not look like a no-op — the buttons
           above re-enable; this strip says why. Clears on the next
           successful mutate. Same dark-card treatment as clockOutError. */}
        {phaseMutation.isError && (
          <div
            className="rounded-md px-3 py-2 text-sm flex items-start gap-2"
            style={{
              backgroundColor: 'rgba(239,68,68,0.18)',
              color: '#FCA5A5',
              border: '1px solid rgba(239,68,68,0.5)',
            }}
            role="alert"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <div className="flex-1 leading-tight">
              <div className="font-medium">Couldn&apos;t change phase</div>
              <div className="text-[12px] opacity-90">
                {phaseMutation.error?.message ?? 'Try again.'}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-2 pt-1">
          <ActionTile
            Icon={Camera}
            label="Photo"
            count={photos.length}
            onClick={() => photoInputRef.current?.click()}
          />
          <ActionTile
            Icon={StickyNote}
            label="Notes"
            count={notesCount}
            onClick={() => setNotesOpen(true)}
          />
          <ActionTile Icon={Barcode} label="Parts" count={parts.length} onClick={() => setPartsOpen(true)} />
          <ActionTile
            Icon={signed ? Check : Signature}
            label="Sign"
            count={signed ? 'ok' : 0}
            onClick={goSign}
          />
        </div>
        {/* The Photo tile opens the camera directly; this is for pictures
           already on the phone (techs shoot in their own camera app so the
           photo also lands in their camera roll). */}
        <button
          type="button"
          onClick={() => libraryInputRef.current?.click()}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium text-white/90 hover:bg-white/10"
        >
          <Images className="h-4 w-4" aria-hidden />
          Add photos from your library
        </button>

        <button
          type="button"
          onClick={() => void onComplete()}
          disabled={(isLead && !signed) || clockOutPending}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold bg-white disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: 'var(--color-live-card-bg)' }}
        >
          {clockOutPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {isLead ? 'Closing out…' : 'Stopping time…'}
            </>
          ) : (
            <>
              <Square className="h-4 w-4" />
              {isLead ? 'Clock out & complete' : 'Stop my time'}
            </>
          )}
        </button>
        {isLead && !signed && !clockOutError && (
          <div className="text-center text-[11px] opacity-80">
            Customer signature required to close
          </div>
        )}
        {/* Multi-visit job with more days booked: stop for the day without
           closing the job. Helpers already have "Stop my time" above. */}
        {isLead && nextVisit && (
          <>
            <button
              type="button"
              onClick={() => endDayMutation.mutate()}
              disabled={endDayMutation.isPending || clockOutPending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium border border-white/40 bg-white/10 hover:bg-white/20 text-white disabled:opacity-50"
            >
              {endDayMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Stopping time…
                </>
              ) : (
                <>Done for today — job continues {shortVisitDay(nextVisit.date)}</>
              )}
            </button>
            {endDayMutation.isError && (
              <div className="text-center text-[11px]" style={{ color: '#FCA5A5' }} role="alert">
                Couldn&apos;t stop your time: {endDayMutation.error.message}
              </div>
            )}
          </>
        )}
        {!isLead && !clockOutError && (
          <div className="text-center text-[11px] opacity-80">
            You're a helper on this visit — the lead handles signature + Done flag.
          </div>
        )}
        {clockOutError && (
          <div
            className="rounded-md px-3 py-2 text-sm flex items-start gap-2"
            style={{
              backgroundColor: 'rgba(239,68,68,0.18)',
              color: '#FCA5A5',
              border: '1px solid rgba(239,68,68,0.5)',
            }}
            role="alert"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <div className="flex-1 leading-tight">
              <div className="font-medium">Couldn&apos;t close out</div>
              <div className="text-[12px] opacity-90">{clockOutError}</div>
              <button
                type="button"
                onClick={() => void onComplete()}
                disabled={clockOutPending}
                className="mt-1.5 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium bg-white/15 hover:bg-white/25 disabled:opacity-50"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </div>

      {orderId !== null && <TimeBreakdownPanel orderId={orderId} />}

      {order && (
        <div className="rounded-lg border bg-surface p-3 space-y-2.5" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <div className="text-xs uppercase tracking-wider text-ink-subtle">
            Work order
          </div>

          <div className="grid grid-cols-2 gap-2 text-[12px]">
            {order.requiredDate && (
              <DetailRow label="Required" value={order.requiredDate} />
            )}
            {order.customerPO && (
              <DetailRow label="Customer PO" value={order.customerPO} mono />
            )}
            {order.contactName && (
              <DetailRow label="Contact" value={order.contactName} />
            )}
            {order.contactPhone && (
              <DetailRow
                label="Phone"
                value={order.contactPhone}
                href={`tel:${order.contactPhone.replace(/[^\d+]/g, '')}`}
                mono
              />
            )}
          </div>

          {site && (site.contactName || site.addressLines.length > 0) && (
            <div className="text-[12px]">
              <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Site</div>
              {site.contactName && <div className="font-medium text-ink">{site.contactName}</div>}
              <div className="text-ink">{site.addressLines.join(' · ') || '—'}</div>
            </div>
          )}

          {order.items.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">
                Parts on this order ({order.items.length})
              </div>
              <ul className="space-y-1">
                {order.items.map((line) => (
                  <li
                    key={line.spireId}
                    className="flex items-start justify-between gap-2 text-[12px]"
                  >
                    <div className="min-w-0">
                      <div className="text-ink truncate">
                        {line.description || line.partNo}
                      </div>
                      <div className="text-[10.5px] font-mono text-ink-muted">
                        {line.partNo} · {line.orderQty}
                        {line.sellMeasure ? ` ${line.sellMeasure}` : ''} × ${line.unitPrice}
                      </div>
                    </div>
                    <span className="text-[12px] tabular-nums shrink-0">
                      ${(Number(line.orderQty) * Number(line.unitPrice)).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between pt-1 text-[12px] font-medium border-t" style={{ borderColor: 'var(--color-border-tertiary)' }}>
            <span>Order total</span>
            <span className="tabular-nums">${order.total}</span>
          </div>
        </div>
      )}

      {parts.length > 0 && (
        <div className="rounded-lg border bg-surface p-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">
            Parts captured this visit
          </div>
          <ul className="space-y-1.5">
            {parts.map((p) => {
              const lineTotal = (Number(p.qty) * Number(p.unitPrice)).toFixed(2);
              return (
                <li key={p.clientId} className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="text-ink font-medium truncate flex items-center gap-1.5">
                      {p.description}
                      <SyncBadge status={p.syncStatus} />
                    </div>
                    <div className="text-[11px] font-mono text-ink-muted">
                      {p.partNo} · {p.qty} {p.uom} × ${p.unitPrice}
                      {p.spireSequence !== null && (
                        <> · <span className="text-emerald-700">line #{p.spireSequence}</span></>
                      )}
                    </div>
                    {p.lastError && (
                      <div className="text-[11px] text-red-700 mt-0.5">{p.lastError}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm tabular-nums">${lineTotal}</span>
                    {p.syncStatus === 'failed' && (
                      <button
                        type="button"
                        onClick={() =>
                          appendPart.mutate({
                            clientId: p.clientId,
                            orderId: orderId,
                            partNo: p.partNo,
                            whse: p.whse,
                            qty: p.qty,
                            unitPrice: p.unitPrice,
                            description: p.description,
                          })
                        }
                        className="text-xs text-ink-accent hover:underline"
                      >
                        Retry
                      </button>
                    )}
                    {p.syncStatus !== 'synced' && p.syncStatus !== 'syncing' && (
                      <button
                        type="button"
                        onClick={() => removePart(p.clientId)}
                        aria-label={`Remove ${p.partNo}`}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-subtle hover:text-red-700"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
            <li className="flex items-center justify-between pt-1 text-sm font-medium border-t" style={{ borderColor: 'var(--color-border-tertiary)' }}>
              <span>Subtotal (parts)</span>
              <span className="tabular-nums">${partsTotal.toFixed(2)}</span>
            </li>
          </ul>
        </div>
      )}

      {photos.length > 0 && (
        <div className="rounded-lg border bg-surface p-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">
            Photos this visit ({photos.length})
          </div>
          <ul className="grid grid-cols-3 gap-2">
            {photos.map((p) => (
              <li key={p.clientId} className="relative">
                <PhotoThumb
                  photo={p}
                  onRetry={() => {
                    if (!p.base64) return;
                    uploadPhoto.mutate({
                      clientId: p.clientId,
                      orderId: orderId,
                      contentType: p.contentType,
                      base64: p.base64,
                      filename: p.filename,
                    });
                  }}
                  onRemove={() => removePhoto(p.clientId)}
                />
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[10.5px] text-ink-subtle">
            Photos save to this job's files and are copied to the order's CRM notes in Spire.
          </div>
        </div>
      )}

      {orderId !== null && (
        <div className="rounded-lg border bg-surface p-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">Notes</div>
          <JobNotes spireId={orderId} emptyHint="No notes yet — sales and field notes show here." />
        </div>
      )}

      {orderId !== null && (
        <div className="rounded-lg border bg-surface p-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">Job files</div>
          {/* Upload stays on the camera tile above (it has the offline outbox behind it). */}
          <JobFiles
            spireId={orderId}
            canUpload={false}
            emptyHint="Nothing attached yet — photos and documents from the office show here."
          />
        </div>
      )}

      <input
        ref={photoInputRef}
        type="file"
        accept="image/png,image/jpeg"
        capture="environment"
        onChange={(e) => {
          void onPhotoSelected(e);
        }}
        className="hidden"
      />
      {/* No `capture`, so the phone offers its photo library; several at once. */}
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/png,image/jpeg"
        multiple
        onChange={(e) => {
          void onPhotoSelected(e);
        }}
        className="hidden"
      />

      <NotesModal
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        onSave={async (body) => {
          if (orderId === null) return;
          await addNoteMutation.mutateAsync({ orderId, body });
        }}
        saving={addNoteMutation.isPending}
        error={addNoteMutation.error?.message ?? null}
      />

      <PartsPicker
        open={partsOpen}
        defaultWhse={meQuery.data?.truckWarehouse ?? order?.items[0]?.whseCode ?? '00'}
        truckWhse={meQuery.data?.truckWarehouse ?? null}
        onClose={() => setPartsOpen(false)}
        onPart={(input) => {
          // Fire the Spire write. Field-job state has already added the part
          // locally with syncStatus='pending' (set inside addPart).
          appendPart.mutate({
            clientId: input.clientId,
            orderId: orderId,
            partNo: input.partNo,
            whse: input.whse,
            qty: input.qty,
            unitPrice: input.unitPrice,
            description: input.description,
          });
        }}
      />
    </div>
  );
}

function PhotoThumb({
  photo,
  onRetry,
  onRemove,
}: {
  photo: import('../../lib/field-job.js').FieldPhotoLocal;
  onRetry: () => void;
  onRemove: () => void;
}) {
  // Show the live preview when we still hold the bytes (pending / syncing /
  // synced-this-session). After a sessionStorage rehydrate the base64 is
  // stripped — fall back to a placeholder.
  const src = photo.base64 ? `data:${photo.contentType};base64,${photo.base64}` : null;
  return (
    <div className="relative aspect-square rounded-md overflow-hidden border" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      {src ? (
        <img src={src} alt="Field-captured" className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full flex items-center justify-center bg-surface-muted text-ink-subtle text-[10px]">
          (no preview)
        </div>
      )}
      <div className="absolute top-1 left-1">
        <SyncBadge status={photo.syncStatus} />
      </div>
      {photo.syncStatus === 'failed' && photo.base64 && (
        <button
          type="button"
          onClick={onRetry}
          className="absolute bottom-1 left-1 inline-flex items-center rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-ink-accent"
        >
          Retry
        </button>
      )}
      {photo.syncStatus !== 'synced' && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove photo"
          className="absolute top-1 right-1 inline-flex h-5 w-5 items-center justify-center rounded bg-white/85 text-ink-muted hover:text-red-700"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      )}
      {photo.lastError && (
        <div className="absolute inset-x-0 bottom-0 bg-red-50/90 text-red-900 text-[10px] px-1 py-0.5 truncate">
          {photo.lastError}
        </div>
      )}
    </div>
  );
}

function DetailRow({
  label,
  value,
  href,
  mono,
}: {
  label: string;
  value: string;
  href?: string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      {href ? (
        <a
          href={href}
          className={`text-ink hover:underline truncate block ${mono ? 'font-mono' : ''}`}
        >
          {value}
        </a>
      ) : (
        <div className={`text-ink truncate ${mono ? 'font-mono' : ''}`}>{value}</div>
      )}
    </div>
  );
}

function SyncBadge({ status }: { status: 'pending' | 'syncing' | 'synced' | 'failed' }) {
  if (status === 'synced') {
    return (
      <span className="inline-flex items-center text-[10px] text-emerald-700" aria-label="Synced to Spire">
        <Check className="h-3 w-3" aria-hidden />
      </span>
    );
  }
  if (status === 'syncing' || status === 'pending') {
    return (
      <span className="inline-flex items-center text-[10px] text-ink-muted" aria-label="Syncing to Spire">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[10px] text-red-700" aria-label="Sync failed">
      <AlertCircle className="h-3 w-3" aria-hidden />
    </span>
  );
}

function PhaseButton({
  label,
  Icon,
  onClick,
  disabled,
}: {
  label: string;
  Icon: typeof Pause;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold border border-white/30 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Icon className="h-4 w-4" aria-hidden />
      <span>{label}</span>
    </button>
  );
}

/** "Tue, Oct 21" for the end-of-day button. */
function shortVisitDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function ActionTile({
  Icon,
  label,
  count,
  onClick,
}: {
  Icon: typeof Camera;
  label: string;
  count: number | string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 rounded-md py-2.5 text-xs font-medium border border-white/30 bg-white/10 hover:bg-white/20 text-white"
    >
      <Icon className="h-5 w-5" aria-hidden />
      <span>{label}</span>
      <span className="text-[10px] rounded-full bg-white/20 px-1.5">{count}</span>
    </button>
  );
}
